'use client';

import { useState, useEffect, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';
import SearchResultCard from './SearchResultCard';

interface DiveSite {
  id: string;
  name: string;
  description: string;
  image: string;
  alt: string;
  depth: string;
  visibility: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  type: 'Shore' | 'Boat';
  marineLife: string[];
  bestSeason: string;
  coordinates: string;
  waterTemp: string;
  entryPoint: string;
  regionId: string;
  regionName: string;
}

interface Species {
  id: number;
  commonName: string;
  scientificName: string;
  category: string;
  image: string;
  alt: string;
  conservationStatus: string;
  sightingProbability: string;
  description: string;
}

interface DiveCentre {
  id: number;
  name: string;
  region: string;
  address: string;
  trainingAgency: string;
  focus: string[];
  season: string;
  website?: string;
  description?: string;
  logo?: string;
}

interface Resource {
  id: string;
  name: string;
  description: string;
  link: string;
  logo: string;
  category: string;
  highlights: string[];
  bestFor: string[];
}

type SearchResult =
{type: 'dive-site';data: DiveSite;} |
{type: 'species';data: Species;} |
{type: 'dive-centre';data: DiveCentre;} |
{type: 'resource';data: Resource;};

const UnifiedSearchInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [contentType, setContentType] = useState<'all' | 'dive-sites' | 'species' | 'dive-centres' | 'resources'>('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Consolidated data from all sources
  const allDiveSites: DiveSite[] = useMemo(() => [
  // Ayia Napa & Protaras sites
  { id: 'green-bay', name: 'Green Bay', description: 'Easiest dive site in the area with walk-in entry. Most suitable for introductory dives and beginners. Rarely there is a current or waves. Divers can see numerous artificial statues at 10m depth. Common practice to feed fish at less than 5m depth. Frequent turtle sightings and hundreds of seabreams around the feeding area.', image: 'https://images.unsplash.com/photo-1720965067898-db4483ddefca', alt: 'Underwater statues on sandy seabed with schools of fish and diver feeding fish in shallow clear water', depth: '5-11m', visibility: '20m+', difficulty: 'Beginner' as const, type: 'Shore' as const, marineLife: ['Sea Turtle', 'Seabream', 'Damselfish', 'Wrasse'], bestSeason: 'Year-round', coordinates: '35°0\'1.13"N / 34°4\'4.51"E', waterTemp: '16-28°C', entryPoint: 'Very easy shore access (walk in), suitable for all car types', regionId: 'famagusta', regionName: 'Ayia Napa & Protaras' },
  { id: 'green-bay-cave', name: 'Green Bay Cave', description: 'Rocky bottom with small through-and-through caverns approximately 10m from entry point. Beautiful caverns with corals, excellent spot for scooter diving (DPV). Lionfish in the caverns along with other cave-specific marine life. Turtles and Moray Eels frequently spotted.', image: "https://images.unsplash.com/photo-1648218789550-1a4095f40a34", alt: 'Diver swimming through small underwater cavern with corals and lionfish visible on rocky walls', depth: '5-17m', visibility: '20m+', difficulty: 'Beginner' as const, type: 'Shore' as const, marineLife: ['Lionfish', 'Moray Eel', 'Sea Turtle', 'Octopus'], bestSeason: 'Year-round', coordinates: '34°59\'57.28"N / 34°4\'23.33"E', waterTemp: '16-28°C', entryPoint: 'Very easy shore access (walk in), suitable for all car types', regionId: 'famagusta', regionName: 'Ayia Napa & Protaras' },
  { id: 'cyclopes-caves', name: 'Cyclopes Caves', description: 'One of the most beautiful dive sites of the area. Rocky, sandy bottom with dramatic drop-off (cliff). The cliff hosts numerous marine life including moray eels, sea urchins, starfish, lionfish, nudibranch, grouper fish, and seabreams.', image: "https://img.rocket.new/generatedImages/rocket_gen_img_122c46257-1777036169406.png", alt: 'Dramatic underwater cliff drop-off with diver descending along rocky wall covered in marine life', depth: '5-30m', visibility: '20m+', difficulty: 'Beginner' as const, type: 'Shore' as const, marineLife: ['Moray Eel', 'Lionfish', 'Grouper', 'Nudibranch'], bestSeason: 'Year-round', coordinates: '34°59\'48.66"N / 34°4\'48.84"E', waterTemp: '16-28°C', entryPoint: 'Easy shore access, suitable for all car types', regionId: 'famagusta', regionName: 'Ayia Napa & Protaras' }
  // Add more dive sites as needed (abbreviated for brevity)
  ], []);

  const allSpecies: Species[] = useMemo(() => [
  { id: 1, commonName: 'Green Sea Turtle', scientificName: 'Chelonia mydas', category: 'Sea Turtles', image: "https://images.unsplash.com/photo-1564652470354-84e08e1df118", alt: 'Green sea turtle swimming gracefully through clear blue Mediterranean waters', conservationStatus: 'Endangered', sightingProbability: 'High', description: 'The Green Sea Turtle is one of the most iconic marine species in Cyprus waters, known for its graceful swimming and gentle nature.' },
  { id: 2, commonName: 'Dusky Grouper', scientificName: 'Epinephelus marginatus', category: 'Fish', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f89b4e64-1772284537801.png", alt: 'Large dusky grouper fish with mottled brown coloring resting near rocky Mediterranean reef', conservationStatus: 'Vulnerable', sightingProbability: 'Moderate', description: 'The Dusky Grouper is a magnificent predatory fish and one of the largest bony fish in the Mediterranean, often curious about divers.' },
  { id: 3, commonName: 'Octopus', scientificName: 'Octopus vulgaris', category: 'Invertebrates', image: 'https://images.unsplash.com/photo-1664984216218-e12c33770d4b', alt: 'Common octopus with reddish-brown skin texture camouflaged against rocky Mediterranean seabed', conservationStatus: 'Least Concern', sightingProbability: 'Very High', description: 'The Common Octopus is one of the most intelligent invertebrates, masters of camouflage and fascinating to observe in Cyprus waters.' }
  // Add more species as needed
  ], []);

  const allDiveCentres: DiveCentre[] = useMemo(() => [
  // Larnaca
  { id: 201, name: 'Dive In Larnaca', region: 'Larnaca', address: 'Lordos Seagate Block A 24 Tasos Mitsopoulos Ave (formerly Piale Pasa) Larnaca 6027 Cyprus', trainingAgency: 'SDI/TDI', focus: ['Beginners to advanced diving', 'Technical diving', 'Zenobia wreck expertise', 'Discover Scuba Diving', 'Premier technical diving centre', 'Wreck diving specialists'], season: 'Open all year round', website: 'https://dive-in.com.cy/', description: 'Premier technical diving centre specialising in beginner to advanced training with expert knowledge of the Zenobia wreck.', logo: '/assets/images/image-1769776158853.png' },
  { id: 202, name: 'Viking Divers', region: 'Larnaca', address: 'Ithakis 8-7081 Larnaca-Dhekelia rd. Pyla, Larnaca, Cyprus', trainingAgency: 'PADI', focus: ['All levels', 'Zenobia trips', 'Wreck diving', 'Year-round diving'], season: 'Open all year round', website: 'https://www.viking-divers.com/', description: 'PADI dive centre offering training for all levels with specialized Zenobia trips and wreck diving experiences.', logo: '/assets/images/image-1769776317943.png' },
  { id: 203, name: 'Alpha Divers', region: 'Larnaca', address: 'Zinonos 1,2, Pyla Gardens, Dhekelia Road, Larnaca, 7081, Cyprus', trainingAgency: 'PADI 5* Career Development Centre', focus: ['Recreational diving - all levels', 'Technical diving', 'Zenobia wreck specialists', 'Side mount training', 'CCR training', 'Professional development'], season: 'Open all year round', website: 'https://alpha-divers.com/', description: 'PADI 5* Career Development Centre specialising in recreational and technical diving at all levels.', logo: '/assets/images/alpha-divers-logo.png' },
  { id: 204, name: 'Pineapple Dive Club', region: 'Larnaca', address: 'Larnaca, Cyprus', trainingAgency: 'PADI', focus: ['Small-group personalized dive trips', 'All levels', 'Zenobia wreck specialization', 'Turtle spotting in Ayia Napa', 'Cape Greco caves and tunnels', 'PADI certification training'], season: 'Open all year round', website: 'https://www.pineapplediveclub.com/', description: 'Local independent dive club specializing in small-group, personalized dive trips for all levels.', logo: '/assets/images/image-1769776624607.png' },
  // Limassol
  { id: 101, name: 'Pissouri Bay Divers', region: 'Limassol', address: '54 Ampelonon Street, Pissouri Bay 4607, Cyprus', trainingAgency: 'PADI', focus: ['Recreational diving', 'Beginners and Discover Scuba Diving', 'Wreck diving', 'Zenobia trips', 'Jubilee Shoals guided dives', 'Private guiding'], season: 'March – November', website: 'https://pissouribaydivers.com/', description: 'Specialising in recreational diving with a strong focus on beginner-friendly experiences and wreck diving adventures.', logo: '/assets/images/download_1_-1769774793392.png' },
  { id: 102, name: 'Aquatic Centre', region: 'Limassol', address: 'Georgiou A4, Germasogeia, 4047, Cyprus', trainingAgency: 'PADI', focus: ['Recreational diving', 'Beginners and Discover Scuba Diving', 'Wreck diving', 'Zenobia trips', 'Limassol wreck dive sites', 'Private guiding upon request'], season: 'March – November', website: 'https://aquatic-centre.com/', description: 'Offering comprehensive recreational diving services with expertise in local Limassol wreck sites and Zenobia expeditions.', logo: '/assets/images/aquatic_dive_academy_1_-1769775608963.jpg' },
  { id: 103, name: 'Pro Dive (ex Blue Thunder Diving)', region: 'Limassol', address: 'Georgiou Griva Digeni 110, Limassol 3101, Cyprus', trainingAgency: 'NAUI', focus: ['Recreational diving', 'Beginner diving and entry-level training', 'Technical diving', 'Guided dive trips', 'Zenobia wreck trips', 'Own dive boat'], season: 'Open all year round', website: 'https://prodive.com.cy/', description: 'Year-round diving operations featuring both recreational and technical diving with dedicated boat services.', logo: '/assets/images/cropped-Prodive-1769774630628.png' },
  { id: 104, name: 'Crest Dive', region: 'Limassol', address: 'Cyprus', trainingAgency: 'PADI', focus: ['Recreational diving services'], season: 'Contact for seasonal information', website: 'https://www.crestdive.com/', description: 'PADI-certified dive centre offering recreational diving services in the Limassol region.', logo: '/assets/images/xCrest_Dive_Logo_landscape-1769775137293.png' },
  // Paphos
  { id: 301, name: 'CyDive', region: 'Paphos', address: '1 Poseidonos Avenue Marina Court 44-468042 Paphos Cyprus', trainingAgency: 'PADI', focus: ['Beginner to advanced level', 'Year-round diving', 'All certification levels', 'Professional training'], season: 'Open all year round', description: 'PADI dive centre offering comprehensive training from beginner to advanced level.', logo: '/assets/images/image-1769776764481.png' },
  { id: 302, name: 'Coral Bay Divers', region: 'Paphos', address: '20 Laxion Street Coral Bay 8575 Pegeia Paphos Cyprus', trainingAgency: 'PADI', focus: ['Beginners to technical diving', 'PADI Career Development Centre', 'All certification levels', 'Professional training programs'], season: 'Open all year round', description: 'PADI Career Development Centre offering comprehensive training from beginners all the way to technical diving.', website: 'https://coralbaydivers.com/', logo: '/assets/images/image-1769776966733.png' }],
  []);

  const allResources: Resource[] = useMemo(() => [
  { id: 'visit-cyprus', name: 'Cyprus Scuba Diving – Official Guide', description: '🇨🇾🤿 The official scuba diving information page of the Cyprus Deputy Ministry of Tourism provides verified, up-to-date guidance on diving in Cyprus, including recognised dive sites, local regulations and safety requirements.', link: 'https://www.visitcyprus.com/discover-cyprus/sports-training/sports-diving/', logo: '/assets/images/visitcyprus-logo_1_-1769759923272.png', category: 'Official Tourism', highlights: ['Official government-backed source', 'Overview of recognised dive sites', 'Local regulations and safety standards'], bestFor: ['First-time visitors', 'Trip planning', 'Understanding official framework'] },
  { id: 'limassol-tourism', name: '🏛️ Limassol Tourism Board – Official Limassol & Scuba Diving Guide', description: 'The official destination and visitor information platform of Limassol provides comprehensive travel guidance for Limassol and surrounding Cyprus region. Includes dedicated scuba diving page for Limassol dive sites and underwater experiences.', link: 'https://www.limassoltourism.com', logo: '/assets/images/356701875_575849974700765_6984119328263317632_n-1769855086139.jpg', category: 'Official Tourism', highlights: ['Official Limassol tourism authority', 'Dedicated scuba diving section', 'Comprehensive travel information'], bestFor: ['Limassol diving planning', 'Limassol holidays', 'First-time Limassol travellers'] },
  { id: 'larnaca-tourism', name: '🏛️ Larnaca Tourism Board – Official Larnaca & Scuba Diving Guide', description: 'The official destination and visitor information platform for the Larnaca region provides comprehensive travel guidance for visitors exploring Larnaca and surrounding coastline. Includes dedicated scuba diving page highlighting dive sites and underwater attractions including the famous Zenobia wreck.', link: 'https://larnakaregion.com/', logo: '/assets/images/445000873_853366006826682_1820013187962434039_n-1769855410333.jpg', category: 'Official Tourism', highlights: ['Official Larnaca tourism authority', 'Dedicated scuba diving section', 'Zenobia wreck coverage'], bestFor: ['Larnaca diving planning', 'Larnaca holidays', 'First-time Larnaca travellers', 'Zenobia wreck diving'] },
  { id: 'dept-fisheries', name: '🐟 Department of Fisheries and Marine Research Cyprus', description: 'Official government authority for marine protection, fisheries management and sustainable use of marine resources in Cyprus. Provides framework for protected dive sites and conservation zones.', link: 'https://www.moa.gov.cy/moa/dfmr/dfmr.nsf/index_en/index_en?OpenDocument', logo: '/assets/images/472145420_911639231158784_2754546932028234573_n-1769854428797.jpg', category: 'Official Tourism', highlights: ['Government marine authority', 'Marine protected areas', 'Artificial reefs oversight'], bestFor: ['Marine conservation', 'Protected dive sites', 'Eco-conscious divers'] },
  { id: 'mer-lab', name: 'Marine & Environmental Research (MER Lab) - Cyprus', description: 'Independent scientific research and environmental consultancy for Cyprus\' marine ecosystems. For more than 15 years, MER has led coastal and offshore research projects across Cyprus.', link: 'https://merresearch.com/', logo: '/assets/images/462617064_3665011177095006_7626686400421776453_n-1769853425926.png', category: 'Research & Science', highlights: ['Leading marine science organisation', 'Government scientific consultant', 'Marine biology and ecology research'], bestFor: ['Marine conservation science', 'Policy and planning projects', 'Researchers and NGOs'] },
  { id: 'enalia-physis', name: '🌿 Enalia Physis Environmental Research Centre', description: 'Non-profit environmental research and conservation organisation in Cyprus, working for the protection of marine and terrestrial ecosystems.', link: 'https://enaliaphysis.org.cy/', logo: '/assets/images/logo_new1_1_-1769853652220.png', category: 'Research & Science', highlights: ['Non-profit environmental organisation', 'Active scientific research', 'Education and public awareness'], bestFor: ['Marine conservation', 'Community groups and NGOs', 'Students and educators'] }],
  []);

  // Search and filter logic
  const filteredResults = useMemo(() => {
    let results: SearchResult[] = [];

    // Filter dive sites
    if (contentType === 'all' || contentType === 'dive-sites') {
      const filteredSites = allDiveSites.filter((site) => {
        const matchesSearch = searchQuery === '' ||
        site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        site.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        site.marineLife.some((ml) => ml.toLowerCase().includes(searchQuery.toLowerCase()));

        const matchesRegion = regionFilter === 'all' || site.regionId === regionFilter;
        const matchesDifficulty = difficultyFilter === 'all' || site.difficulty === difficultyFilter;

        return matchesSearch && matchesRegion && matchesDifficulty;
      });
      results.push(...filteredSites.map((site) => ({ type: 'dive-site' as const, data: site })));
    }

    // Filter species
    if (contentType === 'all' || contentType === 'species') {
      const filteredSpecies = allSpecies.filter((species) => {
        const matchesSearch = searchQuery === '' ||
        species.commonName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        species.scientificName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        species.description.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesCategory = categoryFilter === 'all' || species.category === categoryFilter;

        return matchesSearch && matchesCategory;
      });
      results.push(...filteredSpecies.map((species) => ({ type: 'species' as const, data: species })));
    }

    // Filter dive centres
    if (contentType === 'all' || contentType === 'dive-centres') {
      const filteredCentres = allDiveCentres.filter((centre) => {
        const matchesSearch = searchQuery === '' ||
        centre.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        centre.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        centre.focus.some((f) => f.toLowerCase().includes(searchQuery.toLowerCase()));

        const matchesRegion = regionFilter === 'all' || centre.region === regionFilter;

        return matchesSearch && matchesRegion;
      });
      results.push(...filteredCentres.map((centre) => ({ type: 'dive-centre' as const, data: centre })));
    }

    // Filter resources
    if (contentType === 'all' || contentType === 'resources') {
      const filteredResources = allResources.filter((resource) => {
        const matchesSearch = searchQuery === '' ||
        resource.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesCategory = categoryFilter === 'all' || resource.category === categoryFilter;

        return matchesSearch && matchesCategory;
      });
      results.push(...filteredResources.map((resource) => ({ type: 'resource' as const, data: resource })));
    }

    return results;
  }, [searchQuery, contentType, regionFilter, difficultyFilter, categoryFilter, allDiveSites, allSpecies, allDiveCentres, allResources]);

  const handleReset = () => {
    setSearchQuery('');
    setContentType('all');
    setRegionFilter('all');
    setDifficultyFilter('all');
    setCategoryFilter('all');
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen py-12" style={{ background: '#0A0E14' }}>
        <div className="container mx-auto px-4">
          <div className="animate-pulse space-y-8">
            <div className="h-64 rounded-xl" style={{ background: '#0F2234' }}></div>
            <div className="h-20 rounded-xl" style={{ background: '#0F2234' }}></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="h-64 rounded-xl" style={{ background: '#0F2234' }}></div>
              <div className="h-64 rounded-xl" style={{ background: '#0F2234' }}></div>
              <div className="h-64 rounded-xl" style={{ background: '#0F2234' }}></div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen" style={{ background: '#0A0E14' }}>
      {/* Hero Section */}
      <section
        className="py-12 lg:py-20"
        style={{
          background: 'linear-gradient(180deg, #0B1622 0%, #0A0E14 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.06)'
        }}>

        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div
              className="inline-flex items-center justify-center w-16 h-16 lg:w-20 lg:h-20 rounded-full mb-6"
              style={{ background: 'rgba(111,211,224,0.10)', border: '1px solid rgba(111,211,224,0.20)' }}>

              <Icon name="MagnifyingGlassIcon" size={40} style={{ color: '#6FD3E0' }} />
            </div>
            <h1 className="text-4xl lg:text-5xl font-headline font-bold mb-4" style={{ color: '#EAF4F8' }}>
              Unified Search
            </h1>
            <p className="text-lg lg:text-xl max-w-2xl mx-auto mb-8" style={{ color: '#C8DDE6' }}>
              Search across 48 dive sites, 29 marine species, 9 dive centres, and essential resources
            </p>

            {/* Main Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <Icon
                name="MagnifyingGlassIcon"
                size={24}
                className="absolute left-4 top-1/2 transform -translate-y-1/2"
                style={{ color: '#C8DDE6' }} />

              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search dive sites, marine species, dive centres, or resources..."
                className="w-full pl-14 pr-4 py-4 rounded-xl focus:outline-none text-lg"
                style={{
                  background: '#0F2234',
                  border: '1.5px solid rgba(111,211,224,0.25)',
                  color: '#EAF4F8'
                }} />

            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section
        className="sticky top-16 lg:top-20 z-40"
        style={{
          background: '#0E1C2B',
          borderBottom: '1px solid rgba(255,255,255,0.06)'
        }}>

        <div className="container mx-auto px-4 py-6">
          <div className="space-y-4">
            {/* Content Type Filter */}
            <div className="flex flex-wrap gap-2 justify-center">
              {[
              { id: 'all', label: 'All Results', icon: 'SparklesIcon' },
              { id: 'dive-sites', label: 'Dive Sites', icon: 'MapPinIcon' },
              { id: 'species', label: 'Marine Species', icon: 'FireIcon' },
              { id: 'dive-centres', label: 'Dive Centres', icon: 'BuildingOfficeIcon' },
              { id: 'resources', label: 'Resources', icon: 'BookOpenIcon' }].
              map((type) =>
              <button
                key={type.id}
                onClick={() => setContentType(type.id as any)}
                className="flex items-center space-x-2 px-4 py-2 rounded-full font-medium transition-all duration-200"
                style={
                contentType === type.id ?
                { background: '#6FD3E0', color: '#0B1622', border: '1.5px solid #6FD3E0' } :
                { background: '#0F2234', color: '#C8DDE6', border: '1px solid rgba(255,255,255,0.08)' }
                }
                onMouseEnter={(e) => {
                  if (contentType !== type.id) {
                    (e.currentTarget as HTMLElement).style.border = '1px solid rgba(111,211,224,0.25)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (contentType !== type.id) {
                    (e.currentTarget as HTMLElement).style.border = '1px solid rgba(255,255,255,0.08)';
                  }
                }}>

                  <Icon name={type.icon as any} size={18} />
                  <span>{type.label}</span>
                </button>
              )}
            </div>

            {/* Additional Filters */}
            <div className="flex flex-wrap gap-3 items-center justify-center">
              {/* Region Filter */}
              {(contentType === 'all' || contentType === 'dive-sites' || contentType === 'dive-centres') &&
              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                className="px-4 py-2 rounded-lg focus:outline-none"
                style={{
                  background: '#0F2234',
                  border: '1px solid rgba(111,211,224,0.20)',
                  color: '#EAF4F8'
                }}>

                  <option value="all">All Regions</option>
                  <option value="famagusta">Ayia Napa &amp; Protaras</option>
                  <option value="larnaka">Larnaka</option>
                  <option value="Larnaca">Larnaca</option>
                  <option value="limassol">Limassol</option>
                  <option value="Limassol">Limassol</option>
                  <option value="paphos">Paphos</option>
                  <option value="Paphos">Paphos</option>
                  <option value="latchi">Latchi Akamas</option>
                </select>
              }

              {/* Difficulty Filter */}
              {(contentType === 'all' || contentType === 'dive-sites') &&
              <select
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
                className="px-4 py-2 rounded-lg focus:outline-none"
                style={{
                  background: '#0F2234',
                  border: '1px solid rgba(111,211,224,0.20)',
                  color: '#EAF4F8'
                }}>

                  <option value="all">All Difficulties</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              }

              {/* Category Filter */}
              {(contentType === 'all' || contentType === 'species' || contentType === 'resources') &&
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-4 py-2 rounded-lg focus:outline-none"
                style={{
                  background: '#0F2234',
                  border: '1px solid rgba(111,211,224,0.20)',
                  color: '#EAF4F8'
                }}>

                  <option value="all">All Categories</option>
                  {contentType === 'species' || contentType === 'all' ?
                <>
                      <option value="Fish">Fish</option>
                      <option value="Invertebrates">Invertebrates</option>
                      <option value="Sea Turtles">Sea Turtles</option>
                      <option value="Marine Mammals">Marine Mammals</option>
                      <option value="Rays & Sharks">Rays &amp; Sharks</option>
                    </> :
                null}
                  {contentType === 'resources' || contentType === 'all' ?
                <>
                      <option value="Official Tourism">Official Tourism</option>
                      <option value="Research & Science">Research &amp; Science</option>
                      <option value="NGOs">NGOs</option>
                      <option value="Dive Organizations">Dive Organizations</option>
                      <option value="Conservation">Conservation</option>
                    </> :
                null}
                </select>
              }

              {/* Reset Button */}
              <button
                onClick={handleReset}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200"
                style={{
                  background: '#0F2234',
                  color: '#C8DDE6',
                  border: '1px solid rgba(255,255,255,0.08)'
                }}
                onMouseEnter={(e) => {(e.currentTarget as HTMLElement).style.border = '1px solid rgba(111,211,224,0.25)';}}
                onMouseLeave={(e) => {(e.currentTarget as HTMLElement).style.border = '1px solid rgba(255,255,255,0.08)';}}>

                <Icon name="ArrowPathIcon" size={18} />
                <span>Reset</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-12 lg:py-16">
        <div className="container mx-auto px-4">
          {/* Results Count */}
          <div className="mb-6 flex items-center justify-between">
            <p className="text-lg" style={{ color: '#C8DDE6' }}>
              <span className="font-bold" style={{ color: '#EAF4F8' }}>{filteredResults.length}</span> results found
            </p>
          </div>

          {/* Results Grid */}
          {filteredResults.length > 0 ?
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResults.map((result, index) =>
            <SearchResultCard key={`${result.type}-${index}`} result={result} />
            )}
            </div> :

          <div className="text-center py-16">
              <Icon name="MagnifyingGlassIcon" size={64} style={{ color: '#C8DDE6' }} className="mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2" style={{ color: '#EAF4F8' }}>No results found</h3>
              <p className="mb-6" style={{ color: '#C8DDE6' }}>
                Try adjusting your search query or filters
              </p>
              <button
              onClick={handleReset}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-200"
              style={{ background: '#6FD3E0', color: '#0B1622', border: '1.5px solid #6FD3E0' }}
              onMouseEnter={(e) => {(e.currentTarget as HTMLElement).style.background = '#5ec4d1';}}
              onMouseLeave={(e) => {(e.currentTarget as HTMLElement).style.background = '#6FD3E0';}}>

                <Icon name="ArrowPathIcon" size={20} />
                <span>Reset All Filters</span>
              </button>
            </div>
          }
        </div>
      </section>
    </div>);

};

export default UnifiedSearchInteractive;